import { Component } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { firstValueFrom, Subscription } from 'rxjs';

import { ICluster } from '../../interfaces/cluster.interface';
import { ILimits } from '../../interfaces/limits.interface';
import { IStatistics } from '../../interfaces/statistics.interface';
import { SharedModule } from '../../modules/shared.module';
import { IsNaNPipe } from '../../pipes/is-nan/is-nan.pipe';
import { StatisticsSumPipe } from '../../pipes/statistics-sum/statistics-sum.pipe';
import { FirestoreService } from '../../services/firestore/firestore.service';
import { ClusterCardComponent } from './cluster-card/cluster-card.component';
import { DashboardEmptyComponent } from './dashboard-empty/dashboard-empty.component';
import { EntityCardComponent } from './entity-card/entity-card.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    SharedModule,
    EntityCardComponent,
    AngularFireAuthModule,
    ClusterCardComponent,
    DashboardEmptyComponent,
    IsNaNPipe,
    StatisticsSumPipe,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  public clusters: ICluster[];
  public clustersSub$: Subscription;
  public statistics: Record<string, IStatistics> = {};
  public limits: Record<string, ILimits> = {};

  constructor(
    private firestoreService: FirestoreService,
    private auth: Auth,
  ) {}

  public ngOnInit(): void {
    this.listenToClusters();
  }

  public ngOnDestroy(): void {
    if (this.clustersSub$) this.clustersSub$.unsubscribe();
  }

  public async listenToClusters(): Promise<void> {
    this.clustersSub$ = this.firestoreService
      .getCollectionSnapshot<ICluster>(`customers/${this.auth.currentUser.uid}/clusters`)
      .subscribe((clusters) => {
        this.clusters = clusters;

        this.getStatistics();
        this.getLimits();
      });
  }

  public async getStatistics(): Promise<void> {
    const statistics: Record<string, IStatistics> = {};

    for (const cluster of this.clusters) {
      const statisticsDoc = await firstValueFrom(
        this.firestoreService.getCollectionSnapshot<IStatistics>(
          `customers/${this.auth.currentUser.uid}/clusters/${cluster.id}/statistics`,
          'createdAt',
          'desc',
        ),
      );

      if (statisticsDoc[0]) {
        statistics[cluster.id] = statisticsDoc[0];
        cluster.statistics = statisticsDoc[0];
      }
    }

    this.statistics = statistics;
  }

  public async getLimits(): Promise<void> {
    const limits: Record<string, ILimits> = {};

    for (const cluster of this.clusters) {
      const limitsDoc = await firstValueFrom(
        this.firestoreService.getCollectionSnapshot<ILimits>(
          `customers/${this.auth.currentUser.uid}/clusters/${cluster.id}/limits`,
        ),
      );

      if (limitsDoc[0]) {
        limits[cluster.id] = limitsDoc[0];
        cluster.limits = limitsDoc[0];
      }
    }

    this.limits = limits;
  }
}
